import { useCallback, useEffect, useRef, useState } from "react";

function pickVoice() {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  return (
    voices.find(v => v.lang.startsWith("en") && /google|samantha|karen|daniel/i.test(v.name))
    || voices.find(v => v.lang.startsWith("en"))
    || voices[0]
    || null
  );
}

/**
 * Browser text-to-speech for Z-Cars AI voice replies.
 */
export function useSpeechSynthesis() {
  const [supported] = useState(
    typeof window !== "undefined" && "speechSynthesis" in window,
  );
  const [speaking, setSpeaking] = useState(false);
  const utterRef = useRef(null);

  useEffect(() => {
    if (!supported) return undefined;
    const load = () => pickVoice();
    window.speechSynthesis.onvoiceschanged = load;
    load();
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
      window.speechSynthesis.cancel();
    };
  }, [supported]);

  const stop = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [supported]);

  const speak = useCallback((text) => {
    if (!supported || !text?.trim()) return;
    stop();
    const utter = new SpeechSynthesisUtterance(text.trim().slice(0, 600));
    utter.rate = 1;
    utter.pitch = 1;
    const voice = pickVoice();
    if (voice) utter.voice = voice;
    utter.onstart = () => setSpeaking(true);
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    utterRef.current = utter;
    window.speechSynthesis.speak(utter);
  }, [supported, stop]);

  return { supported, speaking, speak, stop };
}
